import {
  Content,
  Page,
  InfoCard,
  ItemCardHeader,
} from '@backstage/core-components';
import {
  HomePageStarredEntities,
  HomePageToolkit,
} from '@backstage/plugin-home';
import { HomePageSearchBar } from '@backstage/plugin-search';
import { SearchContextProvider } from '@backstage/plugin-search-react';
import {
  Box,
  Button,
  CardContent,
  CardMedia,
  Grid,
  makeStyles,
  Typography,
} from '@material-ui/core';
import LogoHomePage from '../Root/LogoHomePage';
import { FaConfluence, FaGithub, FaJira } from 'react-icons/fa';
import {
  SiGrafana,
  SiMongodb,
  SiPagerduty,
  SiSnyk,
  SiSonarqube,
} from 'react-icons/si';
import { IconContext } from 'react-icons';
import { GiPlasticDuck, GiPolarStar } from 'react-icons/gi';
import { TbBrandX } from 'react-icons/tb';
import { VscAzure, VscAzureDevops } from 'react-icons/vsc';
import { catalogApiRef, EntityRefLink } from '@backstage/plugin-catalog-react';
import { useApi } from '@backstage/core-plugin-api';
import { useAsync } from 'react-use';
import { themeColors } from '../../theme/design-colors.ts';

const useStyles = makeStyles(theme => ({
  searchBarInput: {
    maxWidth: '60vw',
    margin: 'auto',
    backgroundColor: theme.palette.type === 'dark'
        ? themeColors.darkBlueSurfaceAlt
        : themeColors.lightGrey,
    borderRadius: '50px',
    boxShadow: theme.shadows[1],
  },
  searchBarOutline: {
    borderStyle: 'none',
  },
  cardHeader: {
    background: `linear-gradient(135deg, ${themeColors.petrol} 0%, ${themeColors.petrol40k} 100%)`,
    color: themeColors.white,
  },
  heroTitle: {
    color:
      theme.palette.type === 'dark'
        ? theme.palette.text.primary
        : theme.palette.primary.main,
  },
  heroSubtitle: {
    color:
      theme.palette.type === 'dark'
        ? themeColors.lightGrey20k
        : theme.palette.text.secondary,
  },
}));

export const HomePage = () => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);

  // fetches a list of all systems registered on backstage with tag eServices
  const { value: eservicesSystems, error: erroreSvc } = useAsync(async () => {
    const response = await catalogApi.getEntities({
      filter: { kind: 'System', 'metadata.tags': 'eservices' },
    });
    return response.items;
  }, []);

  // fetches a list of all systems registered on backstage with tag Connectivity
  const { value: connectivitySystems, error: errorConn } =
    useAsync(async () => {
      const response = await catalogApi.getEntities({
        filter: { kind: 'System', 'metadata.tags': 'connectivity' },
      });
      return response.items;
    }, []);

  if (erroreSvc) return <div>Error: {erroreSvc.message}</div>;
  if (errorConn) return <div>Error: {errorConn.message}</div>;

  return (
    <SearchContextProvider>
      <Page themeId="home">
        <Content>
          <Grid container justifyContent="center" spacing={6}>
            <Grid item>
              <Box
                mt={5}
                display="flex"
                flexDirection="column"
                alignItems="center"
              >
                <LogoHomePage />
                <Box mt={5}>
                  <Typography variant="h1" className={classes.heroTitle} align="center">
                    SPOTLIGHT
                  </Typography>
                  <Typography variant="subtitle2" className={classes.heroSubtitle} align="center">
                    Internal Developer Portal developed by tb.lx
                  </Typography>
                  <Typography align="center">
                    <p />
                    <Button
                      style={{
                        blockSize: '23px',
                        fontSize: '12px',
                        backgroundColor: themeColors.petrol20,
                        color: themeColors.petrol40k,
                      }}
                      size="small"
                      variant="contained"
                      disabled
                    >
                      Beta
                    </Button>
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid container item xs={12} justifyContent="center">
              <HomePageSearchBar
                InputProps={{
                  classes: {
                    root: classes.searchBarInput,
                    notchedOutline: classes.searchBarOutline,
                  },
                }}
                placeholder="Search"
              />
            </Grid>

            <Grid container item xs={12} md={12}>
              <Grid item xs={12} md={12} xl={4}>
                <InfoCard title="Available Products">
                  <Grid container xs={12}>
                    <Grid item xs={6}>
                      <CardMedia>
                        <ItemCardHeader
                          title="eServices"
                          subtitle=""
                          classes={{
                            root: classes.cardHeader,
                          }}
                        />
                      </CardMedia>
                      <CardContent>
                        {eservicesSystems?.map(system => (
                          <Typography
                            key={system.metadata.uid || system.metadata.name}
                            variant="h6"
                          >
                            <EntityRefLink
                              entityRef={system}
                              defaultKind="System"
                            />
                          </Typography>
                        ))}
                      </CardContent>
                    </Grid>
                    <Grid item xs={6}>
                      <CardMedia>
                        <ItemCardHeader
                          title="Connectivity"
                          subtitle=""
                          classes={{
                            root: classes.cardHeader,
                          }}
                        />
                        <CardContent>
                          {connectivitySystems?.map(system => (
                            <Typography
                              key={system.metadata.uid || system.metadata.name}
                              variant="h6"
                            >
                              <EntityRefLink
                                entityRef={system}
                                defaultKind="System"
                              />
                            </Typography>
                          ))}
                        </CardContent>
                      </CardMedia>
                    </Grid>
                  </Grid>
                </InfoCard>
              </Grid>

              <Grid item xs={12} md={6} xl={4}>
                <HomePageToolkit
                  tools={[
                    {
                      url: 'https://git.t3.daimlertruck.com',
                      label: 'Github T3',
                      icon: (
                        <IconContext.Provider value={{ size: '2em' }}>
                          <FaGithub />
                        </IconContext.Provider>
                      ),
                    },
                    {
                      url: 'https://github.com',
                      label: 'Github',
                      icon: (
                        <IconContext.Provider value={{ size: '2em' }}>
                          <FaGithub />
                        </IconContext.Provider>
                      ),
                    },
                    {
                      url: 'https://jir.t3.daimlertruck.com',
                      label: 'Jira',
                      icon: (
                        <IconContext.Provider value={{ size: '2em' }}>
                          <FaJira />
                        </IconContext.Provider>
                      ),
                    },
                    {
                      url: 'https://con.t3.daimlertruck.com',
                      label: 'Confluence',
                      icon: (
                        <IconContext.Provider value={{ size: '2em' }}>
                          <FaConfluence />
                        </IconContext.Provider>
                      ),
                    },
                    {
                      url: 'https://app.eu.snyk.io/org/tt-titt-eservices/integrations',
                      label: 'Snyk',
                      icon: (
                        <IconContext.Provider value={{ size: '2em' }}>
                          <SiSnyk />
                        </IconContext.Provider>
                      ),
                    },
                    {
                      url: 'https://son.t3.daimlertruck.com/projects',
                      label: 'SonarQube',
                      icon: (
                        <IconContext.Provider value={{ size: '2em' }}>
                          <SiSonarqube />
                        </IconContext.Provider>
                      ),
                    },
                    {
                      url: 'https://bla.t3.daimlertruck.com',
                      label: 'BlackDuck',
                      icon: (
                        <IconContext.Provider value={{ size: '2em' }}>
                          <GiPlasticDuck />
                        </IconContext.Provider>
                      ),
                    },
                    {
                      url: 'https://cloud.mongodb.com',
                      label: 'Mongo Atlas',
                      icon: (
                        <IconContext.Provider value={{ size: '2em' }}>
                          <SiMongodb />
                        </IconContext.Provider>
                      ),
                    },
                    {
                      url: 'https://csgproducts.grafana.csg.daimlertruck.com',
                      label: 'Grafana',
                      icon: (
                        <IconContext.Provider value={{ size: '2em' }}>
                          <SiGrafana />
                        </IconContext.Provider>
                      ),
                    },
                    {
                      url: 'https://app.getdx.com/core4',
                      label: 'DX',
                      icon: (
                        <IconContext.Provider value={{ size: '2em' }}>
                          <TbBrandX />
                        </IconContext.Provider>
                      ),
                    },
                    {
                      url: 'https://dtcsg.eu.pagerduty.com/incidents',
                      label: 'PagerDuty',
                      icon: (
                        <IconContext.Provider value={{ size: '2em' }}>
                          <SiPagerduty />
                        </IconContext.Provider>
                      ),
                    },
                    {
                      url: 'https://portal.azure.com/#home',
                      label: 'Azure Portal',
                      icon: (
                        <IconContext.Provider value={{ size: '2em' }}>
                          <VscAzure />
                        </IconContext.Provider>
                      ),
                    },
                    {
                      url: 'https://dev.azure.com/DT-tblx',
                      label: 'DevOps',
                      icon: (
                        <IconContext.Provider value={{ size: '2em' }}>
                          <VscAzureDevops />
                        </IconContext.Provider>
                      ),
                    },
                    {
                      url: 'https://start.weu.ops.runtime.csg.daimlertruck.com',
                      label: 'CSG StartPage',
                      icon: (
                        <IconContext.Provider value={{ size: '2em' }}>
                          <GiPolarStar />
                        </IconContext.Provider>
                      ),
                    },
                  ]}
                />
              </Grid>

              <Grid item xs={12} md={6} xl={4}>
                <HomePageStarredEntities />
              </Grid>
            </Grid>
          </Grid>
        </Content>
      </Page>
    </SearchContextProvider>
  );
};
